import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.76, 0, 0.24, 1];

// Masked line-by-line reveal for headlines.
export function MaskedLines({ lines, className = "", delay = 0, as: Tag = "h1" }) {
  return (
    <Tag className={className}>
      <span className="sr-only">{lines.join(" ")}</span>
      {lines.map((line, i) => (
        <span className="mask-line" key={i} aria-hidden="true">
          <motion.span
            style={{ display: "block" }}
            initial={{ y: "135%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, ease: EASE, delay: delay + i * 0.12 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

// Character-level masked reveal for hero-scale headlines.
export function MaskedChars({ lines, className = "", delay = 0, as: Tag = "h1", stagger = 0.02 }) {
  let count = 0;
  return (
    <Tag className={className}>
      <span className="sr-only">{lines.join(" ")}</span>
      {lines.map((line, li) => {
        const words = line.split(" ");
        return (
          <span key={li} className="block" aria-hidden="true">
            {words.map((word, wi) => (
              <span key={wi} className="inline-block whitespace-nowrap">
                {word.split("").map((ch, ci) => {
                  const d = delay + count++ * stagger;
                  return (
                    <span key={ci} className="mask-char" style={{ marginRight: "-0.05em" }}>
                      <motion.span
                        className="inline-block"
                        initial={{ y: "135%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 0.9, ease: EASE, delay: d }}
                      >
                        {ch}
                      </motion.span>
                    </span>
                  );
                })}
                {wi < words.length - 1 ? <span className="inline-block w-[0.22em]" /> : null}
              </span>
            ))}
          </span>
        );
      })}
    </Tag>
  );
}

// Scroll-into-view reveal.
export function Reveal({ children, className = "", delay = 0, y = 40, once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

// Reveal that masks a single line of text on scroll.
export function RevealMask({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px -10% 0px" });
  return (
    <Tag ref={ref} className={`mask-line ${className}`}>
      <motion.span
        style={{ display: "block" }}
        initial={{ y: "135%" }}
        animate={{ y: inView ? "0%" : "135%" }}
        transition={{ duration: 1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </Tag>
  );
}
